import { stripDiacritics } from './stringManipulation'

export interface PersonFirstLast {
  first: string
  last: string
}

interface BorrowerName {
  firstName?: string | null
  lastName?: string | null
}

const NAME_SUFFIXES = new Set(['JR', 'SR', 'II', 'III', 'IV', 'V'])
const JOINT_CONNECTOR = /&|\b(AND|OR|POD|ITF)\b/i

export function isNameSuffix(word: string): boolean {
  return NAME_SUFFIXES.has(word.toUpperCase().replace(/[^A-Z0-9]/g, ''))
}

function normalizedNameWords(name: string | null | undefined): string[] {
  if (!name || typeof name !== 'string') {
    return []
  }

  return stripDiacritics(name)
    .toUpperCase()
    .split(/[\s-]+/)
    .map((word) => word.replace(/[^A-Z0-9]/g, ''))
    .filter((word) => word.length > 0)
    .filter((word, index) => index === 0 || !NAME_SUFFIXES.has(word))
}

export function normalizePersonNameForMatching(
  name: string | null | undefined
): string {
  return normalizedNameWords(name).join(' ')
}

export function parsePersonFirstLast(
  fullName: string | null | undefined
): PersonFirstLast | null {
  const words = normalizedNameWords(fullName)
  if (words.length < 2) {
    return null
  }

  return { first: words[0], last: words[words.length - 1] }
}

function borrowerFirstLast(borrower: BorrowerName): PersonFirstLast | null {
  const first = normalizedNameWords(borrower.firstName)[0]
  const lastWords = normalizedNameWords(borrower.lastName)
  const last = lastWords[lastWords.length - 1]
  if (!first || !last) {
    return null
  }

  return { first, last }
}

/**
 * First+last equality after normalization; joint-owner strings (AND, &, POD, ...) never match.
 */
export function personNameMatchesBorrower(
  fullName: string | null | undefined,
  borrower: BorrowerName
): boolean {
  if (fullName && JOINT_CONNECTOR.test(fullName)) {
    return false
  }

  const borrowerFirstLastName = borrowerFirstLast(borrower)
  const ownerFirstLastName = parsePersonFirstLast(fullName)
  if (!borrowerFirstLastName || !ownerFirstLastName) {
    return false
  }

  return (
    ownerFirstLastName.first === borrowerFirstLastName.first &&
    ownerFirstLastName.last === borrowerFirstLastName.last
  )
}
